import { Toaster } from "@/components/ui/toaster"
import { Analytics } from "@vercel/analytics/react"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider } from '@/lib/AuthContext';
import Login from './pages/Login';

// Gate & Providers
import RootGate from '@/components/RootGate';
import { BackButtonProvider } from '@/lib/BackButtonProvider';

// Layouts
import Layout from './Layout.jsx';
import TabsLayout from './layouts/TabsLayout.jsx';

// Pages (Tab pages are imported by TabsLayout directly)
import Notifications from './pages/Notifications';
import OpenDuels from './pages/OpenDuels';
import AddPlayground from './pages/AddPlayground';
import CourtDetail from './pages/CourtDetail';
import Privacy from './pages/Privacy';
import DeleteAccount from './pages/DeleteAccount';

// Admin Pages
import Admin from './pages/Admin';
import AdminChallenges from './pages/AdminChallenges';
import AdminChallengeDetail from './pages/AdminChallengeDetail';
import AdminCourts from './pages/AdminCourts';
import AdminUsers from './pages/AdminUsers';
import AdminLogs from './pages/AdminLogs';
import AdminCodes from './pages/AdminCodes';

// Verifie si on est sur une route d'onglet (TabsLayout reste monte)
function isTabRoute(pathname) {
  return pathname === '/' ||
         pathname === '/map' ||
         pathname === '/feed' ||
         pathname === '/challenges' ||
         pathname === '/profile';
}

/**
 * MainApp - Routes de l'application principale
 *
 * Ce composant ne s'affiche QUE si l'utilisateur est:
 * - Authentifie
 * - Onboarding complete
 * - Statut ACTIVE
 *
 * La logique d'auth/onboarding est geree par RootGate
 */
const MainApp = () => {
  const location = useLocation();
  const isOnTabRoute = isTabRoute(location.pathname);

  return (
    <Layout currentPageName={location.pathname}>
      {/* TabsLayout toujours monte, cache quand sur une page standalone */}
      <div style={{ display: isOnTabRoute ? 'contents' : 'none' }}>
        <TabsLayout />
      </div>

      {/* Routes standalone - affichees par-dessus quand actives */}
      {!isOnTabRoute && (
        <Routes>
          {/* Court detail */}
          <Route path="/court/:courtId" element={<CourtDetail />} />

          {/* Standalone pages */}
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/open-duels" element={<OpenDuels />} />
          <Route path="/add-playground" element={<AddPlayground />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />

          {/* Admin routes */}
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/challenges" element={<AdminChallenges />} />
          <Route path="/admin/challenges/:challengeId" element={<AdminChallengeDetail />} />
          <Route path="/admin/courts" element={<AdminCourts />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/logs" element={<AdminLogs />} />
          <Route path="/admin/codes" element={<AdminCodes />} />

          {/* Legacy routes - redirect to new paths */}
          <Route path="/Home" element={<Navigate to="/map" replace />} />
          <Route path="/Profile" element={<Navigate to="/profile" replace />} />
          <Route path="/Challenges" element={<Navigate to="/challenges" replace />} />
          <Route path="/LocalFeed" element={<Navigate to="/feed" replace />} />
          <Route path="/Notifications" element={<Navigate to="/notifications" replace />} />
          <Route path="/OpenDuels" element={<Navigate to="/open-duels" replace />} />
          <Route path="/AddPlayground" element={<Navigate to="/add-playground" replace />} />
          <Route path="/Admin" element={<Navigate to="/admin" replace />} />
          <Route path="/AdminChallenges" element={<Navigate to="/admin/challenges" replace />} />
          <Route path="/AdminChallengeDetail" element={<Navigate to="/admin/challenges" replace />} />
          <Route path="/AdminCourts" element={<Navigate to="/admin/courts" replace />} />
          <Route path="/AdminUsers" element={<Navigate to="/admin/users" replace />} />
          <Route path="/AdminLogs" element={<Navigate to="/admin/logs" replace />} />
          <Route path="/AdminCodes" element={<Navigate to="/admin/codes" replace />} />

          {/* Onboarding redirect */}
          <Route path="/onboarding" element={<Navigate to="/map" replace />} />
          <Route path="/Onboarding" element={<Navigate to="/map" replace />} />

          {/* 404 */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      )}
    </Layout>
  );
};


function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={
              <BackButtonProvider>
                <RootGate>
                  <MainApp />
                </RootGate>
              </BackButtonProvider>
            } />
          </Routes>
        </Router>
        <Toaster />
        <Analytics />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
